import ItemData from 'src/assets/ItemData'
import ItemDataLive from 'src/assets/ItemDataLive'
import arrayShuffle from 'src/assets/arrayShuffle'
import { Item, ItemTypeTopic, SessionAttendee } from 'src/graphql/types'
import latinize from 'latinize'
import seedrandom from 'seedrandom'


/* ***************************************************************************
 * types
 * ***************************************************************************/


export type SlideId = string
export type AnswerId = string
export type SlideGroupId = string

export type Options = {
  [key: string]: any
}

export type Answer = Options & {
  id: AnswerId
  sort: number
  hide?: boolean
  deletedAt?: string
}

export type Slide = Options & {
  id: SlideId
  sort: number
  type: string
  text: string
  shuffle?: boolean
  hide?: boolean
  deleted?: string
  answers?: { [key: string]: Answer }
  randomizeOff?: boolean
  randomizeLastOff?: boolean
  pointsCorrect?: number
  pointsWrong?: number
}

export type Group = {
  shuffle?: boolean
  merge?: boolean
  count?: number
}

export type Groups = {
  [key: string]: Group
}

export type Subgroup = {
  id: SlideGroupId
  shuffle: boolean
  merge: boolean
  slides: Slide[]
}

export type Slides = {
  [key: string]: Slide
}

export type SlidesPage = {
  id: string
  slides: Slide[]
  timer: number
}

export type SlidesSettings = Slides & {
  groups?: Groups
}

export type AttendeeResponse = { [key: string]: boolean | string } | string[] | number

export type ResponseMeta = {
  currSlideId: SlideId
  lastActiveAt: string
  shuffle: SlideId[]
  params: { [key: string]: string }
}

export type ResponseSlide = {
  timems: number
  response?: AttendeeResponse
}

export type Response = ResponseMeta & {
  [key: string]: ResponseSlide
}

export type SlideParams = {
  [key: string]: { [key: string]: string[] | number[] }
}

export type SlideParamsValuesSet = {
  [key: string]: string | number
}

export type SlideParamsValues = {
  [key: string]: SlideParamsValuesSet
}

export type SlideCorrectResponse = string[]

export type SlidesCorrectResponses = {
  [key: string]: SlideCorrectResponse
}

export type QuizSlideResult = {
  points: number
  pointsMax: number
  wrong: { [key: string]: boolean }
  correct: { [key: string]: boolean | number | string }
}


/* ***************************************************************************
 * SlideAnswers
 * ***************************************************************************/


export class SlideAnswers {
  answers: Answer[] = []

  constructor (slide: Slide, seed: string | undefined) {
    if (slide.answers) {
      const answers = Object.values(slide.answers).filter((answer: Answer) => !answer.deleted).sort( (a: Answer, b: Answer) => a.sort - b.sort )
      if (!seed || slide.randomizeOff) {
        this.answers = answers
      } else {
        let last: Answer | undefined = undefined
        if (slide.randomizeLastOff) {
          last = answers.pop()
        }
        // ------------ shuffle --------------
        const random = seedrandom.alea(seed + slide.id)
        const shuffled = answers.map((answer: Answer) => { return { answer, sort: random() } })
        this.answers.push(...shuffled.sort( (a, b) => a.sort - b.sort ).map( x => x.answer ))
        if (last) {
          this.answers.push(last)
        }
      }
    }
  }
}


/* ***************************************************************************
 * AttendeeItemData
 * ***************************************************************************/


export class AttendeeItemData {

  itemData!: ItemData
  attendee!: SessionAttendee

  slides!: Slide[]
  pages!: SlidesPage[]
  meta!: string[]
  params!: AttendeeResponseParams

  constructor (typeTopic: ItemTypeTopic, itemData: ItemData, attendee: SessionAttendee) {
    this.itemData = itemData
    this.attendee = attendee

    if (typeTopic === ItemTypeTopic.Keynote) {
      this.constructKeynote(itemData)
    } else {
      this.constructQuiz(itemData, attendee.data as Response)
    }
  }

  constructKeynote (itemData: ItemData) {
    const slidesAll = Object.values(itemData.slides) as Slide[]
    this.slides =
      slidesAll
        .filter(s => s.id && !s.deleted && !s.hide && s.type !== 'Params')
          .reduce(
            (result: Slide[], slide: Slide) => [
              ...result,
              ...(
                slide.meta?.pages
                  ? slide.meta.pages
                    .filter((page: Json) => !page.deleted)
                      .map(
                        (page: Json, index: number) =>
                          Object.assign({}, slide, page, {
                            id: `${slide.id}#${page.pageNo}`,
                            meta: { masterSlide: slide, masterSlidePageIndex: index }
                          })
                      )
                  : [slide]
              )
            ],
            [] as Slide[])
            .sort((a, b) => a.sort - b.sort)
    this.params = new AttendeeResponseParams(itemData)
  }

  constructQuiz (itemData: ItemData, response: Response) {
    const responseSlides = new AttendeeResponseSlides(itemData, response)
    this.slides = responseSlides.getSlides()
    this.pages = responseSlides.getPages()
    this.meta = responseSlides.getShuffleMeta()
    this.params = new AttendeeResponseParams(itemData)
  }
}


/* ***************************************************************************
 * AttendeeResponseSlides
 * ***************************************************************************/


export class AttendeeResponseSlides {
  subgroups: Subgroup[] = []

  constructor (itemData: ItemData | ItemDataLive, response: Response) {
    const slidesAll = Object.values(itemData.slides) as Slide[]
    const slides =
      slidesAll
        .filter(s => s.id && !s.deleted && !s.hide && s.type !== 'Params')
          .reduce(
            (result: Slide[], slide: Slide) => [
              ...result,
              ...(
                slide.meta?.pages
                  ? slide.meta.pages
                    .filter((page: Json) => !page.deleted)
                      .map(
                        (page: Json, index: number) =>
                          Object.assign({}, slide, page, {
                            id: `${slide.id}#${page.pageNo}`,
                            meta: { masterSlide: slide, masterSlidePageIndex: index }
                          })
                      )
                  : [slide]
              )
            ],
            [] as Slide[])
            .sort((a, b) => a.sort - b.sort)
    const groups = itemData.slides.groups || {}
    const slidesShuffledByGroup = this.shuffleAndLimitSlidesByGroups(slides, response, groups)
    const subgroups = this.mergeIntoSubGroups(slides, slidesShuffledByGroup, groups)
    this.subgroups = this.shuffleSubgroups(subgroups, response)
  }

  shuffleAndLimitSlidesByGroups (slides: Slide[], response: Response, groupsById: Groups): Map<SlideGroupId, Slide[]> {
    const slidesToShuffleByGroup = new Map<SlideGroupId, Slide[]>()
    for (const slide of slides) {
      if (slide.group && slide.shuffle) {
        const group = slidesToShuffleByGroup.get(slide.group) || []
        group.push(slide)
        slidesToShuffleByGroup.set(slide.group, group)
      }
    }
    const slidesShuffledByGroup = new Map<SlideGroupId, Slide[]>()
    for (const [slideGroupId, slidesToShuffle] of slidesToShuffleByGroup) {
      const slidesShuffled = this.shuffleAndLimitSlides(slidesToShuffle, response, groupsById[slideGroupId]?.count)
      slidesShuffledByGroup.set(slideGroupId, slidesShuffled)
    }
    return slidesShuffledByGroup
  }

  shuffleAndLimitSlides (slides: Slide[], response: Response, limit: number | undefined): Slide[] {
    const slidesById = new Map<SlideId, Slide>(slides.map(slide => [slide.id, slide]))
    const shuffled: Slide[] = []
    for (const slideId of response.shuffle || []) {
      const slide = slidesById.get(slideId)
      if (slide) {
        shuffled.push(slide)
        slidesById.delete(slideId)
      }
    }
    const responded: Slide[] = [...slidesById.values()].filter(slide => response[slide.id] !== undefined)
    const unresponded = [...slidesById.values()].filter(slide => response[slide.id] === undefined)
    arrayShuffle(responded)
    arrayShuffle(unresponded)
    shuffled.push(...responded, ...unresponded)
    return shuffled.slice(0, limit || shuffled.length)
  }

  mergeIntoSubGroups (slidesAll: Slide[], slidesShuffledByGroup: Map<SlideGroupId, Slide[]>, groupsById: Groups) {
    const subgroups = new Map<string, Subgroup>()
    const counters = new Map<string, number>()
    let groupId
    for (const slide of slidesAll) {
      if (slide.group) {
        let counter = counters.get(slide.group) || 0
        if (slide.group !== groupId) {
          counters.set(slide.group, ++counter)
        }
        const name = slide.group + counter
        const subgroup = subgroups.get(name) || {
          id: name,
          shuffle: groupsById[slide.group]?.shuffle || false,
          merge:  groupsById[slide.group]?.merge || false,
          slides: []
        } as Subgroup
        if (slide.shuffle) {
          const shuffledSlide = slidesShuffledByGroup.get(slide.group)?.shift()
          if (shuffledSlide) {
            subgroup.slides.push(shuffledSlide)
          }
        } else {
          subgroup.slides.push(slide)
        }
        subgroups.set(name, subgroup)
      } else {
        subgroups.set(slide.id, {
          id: slide.id,
          shuffle: slide.shuffle || false,
          merge: false,
          slides: [slide]
        })
      }
    }
    return subgroups
  }

  shuffleSubgroups (subgroupsById: Map<SlideGroupId, Subgroup>, response: Response) {

    const subgroupsToShuffle = new Map<SlideGroupId, Subgroup>()
    for (const [id, subgroup] of subgroupsById) {
      if (subgroup.shuffle) {
        subgroupsToShuffle.set(id, subgroup)
      }
    }

    const shuffled: Subgroup[] = []
    for (const id of response.shuffle || []) {
      const subgroup = subgroupsToShuffle.get(id)
      if (subgroup) {
        shuffled.push(subgroup)
        subgroupsToShuffle.delete(id)
      }
    }

    const rest: Subgroup[] = [...subgroupsToShuffle.values()]
    arrayShuffle(rest)
    shuffled.push(...rest)

    const result: Subgroup[] = []
    for (const subgroup of subgroupsById.values()) {
      if (subgroup.shuffle) {
        const s = shuffled.shift()
        if (s && s.slides.length) {
          result.push(s)
        }
      } else if (subgroup.slides.length) {
        result.push(subgroup)
      }
    }

    return result
  }

  getShuffleMeta () {
    const meta: string[] = []
    for (const group of this.subgroups) {
      if (group.shuffle && group.slides[0].id !== group.id) {
        meta.push(group.id)
      }
      for (const slide of group.slides) {
        if (slide.shuffle) {
          meta.push(slide.id)
        }
      }
    }
    return meta
  }

  getSlides () {
    const slides: Slide[] = []
    for (const group of this.subgroups) {
      for (const slide of group.slides) {
        slides.push(slide)
      }
    }
    return slides
  }

  getPages () {
    const pages: SlidesPage[] = []
    for (const group of this.subgroups) {
      if (group.merge) {
        pages.push({
          id: group.id,
          slides: [...group.slides],
          timer: group.slides.reduce((timer, slide) => { return timer + (slide.timer || 0) }, 0)
        })
      } else {
        for (const slide of group.slides) {
          pages.push({
            id: slide.id,
            slides: [slide],
            timer: slide.timer || 0
          })
        }
      }
    }
    return pages
  }
}


/* ***************************************************************************
 * AttendeeResponseParams
 * ***************************************************************************/


export class AttendeeResponseParams {

  slides: Slide[] = []
  params: SlideParams = {}

  constructor (itemData: ItemData) {
    this.slides = Object.values(itemData.slides).filter(s => s.id && s.type === 'Params' && !s.deleted) as Slide[]
    this.params = itemData.corrects
  }

  getMeta (response: Response) {
    const meta = response.params || {}
    for (const slide of this.slides) {
       const rand = slide.rand || {}
       if (!meta[ slide.id ] || !rand[ meta[ slide.id ] ]) {
          const ids = Object.keys(rand)
          arrayShuffle(ids)
          const rowId = ids.pop()
          if (rowId) {
            meta[ slide.id ] = rowId
          }
       }
    }
    return meta
  }

  getValues (meta: { [key: string]: string }) {
    const values: SlideParamsValues = {}

    for (const slide of this.slides) {
      const params: { [key: string]: string | number } = {}

      const rand = slide.rand ? slide.rand[ meta[ slide.id ] ] || [] : []
      const slidex = slide.x || []
      for (let index = 0; index < slidex.length ; index++) {
        const name = slidex[ index ]
        const value = index < rand.length ? rand[ index ] : ''
        if (value !== '') {
          params[ `X${index+1}` ] = value
          if (name) {
            params[ name ] = value
          }
        }
      }

      const correct = this.params[ slide.id ] ? this.params[ slide.id ][ meta[ slide.id ] ] || [] : []
      const slidey = slide.y || []
      for (let index = 0; index < slidey.length ; index++) {
        const name = slidey[ index ]
        const value = index < correct.length ? correct[ index ] : ''
        if (value !== '') {
          params[ `Y${index+1}` ] = value
          if (name) {
            params[ name ] = value
          }
        }
      }

      values[ slide.id ] = params
    }

    return values
  }
}


/* ***************************************************************************
 * AttendeeResponseResult
 * ***************************************************************************/


export class AttendeeResponseResult {

  attendeeItemData!: AttendeeItemData

  questions!: Map<string, Slide>
  corrects!: SlidesCorrectResponses
  params!: SlideParamsValues

  percent = 0
  points = 0
  pointsMax = 0
  slides: { [key: string]: QuizSlideResult | undefined } = {}

  constructor (attendeeItemData: AttendeeItemData) {
    this.attendeeItemData = attendeeItemData
    const questions = attendeeItemData.slides.filter(s => ['Choice', 'YesNo', 'Order', 'Number', 'Gaps', 'Essay'].includes(s.type))
    this.questions = new Map(questions.map(q => [q.id, q]))
    this.corrects = attendeeItemData.itemData.corrects as SlidesCorrectResponses
    const meta = attendeeItemData.params.getMeta(attendeeItemData.attendee.data as Response)
    this.params = attendeeItemData.params.getValues(meta)
    this.checkResponse(attendeeItemData.attendee.data as Response)
  }

  checkResponse (response: Response) {
    Object.assign(this.attendeeItemData.attendee.data, response)
    for (const question of this.questions.values()) {
      switch (question.type) {
        case 'Choice':
          this.slides[ question.id ] = this.checkChoice(question, response[ question.id ]?.response, this.corrects[ question.id ])
          break
        case 'YesNo':
          this.slides[ question.id ] = this.checkYesNo(question, response[ question.id ]?.response, this.corrects[ question.id ])
          break
        case 'Order':
          this.slides[ question.id ] = this.checkOrder(question, response[ question.id ]?.response, this.corrects[ question.id ])
          break
        case 'Number':
          this.slides[ question.id ] = this.checkNumber(question, response[ question.id ]?.response, this.corrects[ question.id ], this.params[ question.params ])
          break
        case 'Gaps':
          this.slides[ question.id ] = this.checkGaps(question, response[ question.id ]?.response, this.corrects[ question.id ])
          break
        case 'Essay':
          this.slides[ question.id ] = this.checkEssay(question)
          break
      }
    }
    this.percent = 0
    this.points = 0
    this.pointsMax = 0
    for (const key in this.slides) {
      const result = this.slides[ key ]
      if (result) {
        this.points += result.points
        this.pointsMax += result.pointsMax
      }
    }
    if (this.pointsMax) {
      this.percent = 100.0 * this.points / this.pointsMax
    }
  }

  checkChoice(slide: Slide, response: AttendeeResponse | undefined, correctResponse: SlideCorrectResponse | undefined) {

    if (correctResponse) {
      const result: QuizSlideResult = {
        points: 0,
        pointsMax: 0,
        wrong: {},
        correct: {}
      }
      const pointsCorrect = slide.pointsCorrect === undefined ? 1 : slide.pointsCorrect
      const pointsWrong = slide.pointsWrong === undefined ? 0 : slide.pointsWrong
      let answersCorrectCount = 0
      let answersRespondCount = 0
      response = (response || {}) as { [key: string]: boolean }
      correctResponse = correctResponse || []

      for (const answer of  Object.values(slide.answers || {}).filter((answer: Answer) => !answer.deleted)) {
        const correct = correctResponse.indexOf(answer.id) !== -1
        const responded = response[ answer.id ] === true

        if (correct) {
          answersCorrectCount++
          result.correct[ answer.id ] = true
        }
        if (responded) {
          answersRespondCount++
        }

        if (+correct ^ +responded) {
          result.wrong[ answer.id ] = true
          result.points += pointsWrong
        } else if (responded) {
          result.points += pointsCorrect
        }
      }

      if (slide.multichoice) {
        if (slide.pointsPerAnswer) {
          result.pointsMax = pointsCorrect * answersCorrectCount
        } else {
          result.pointsMax = pointsCorrect
          if (Object.keys(result.wrong).length) {
            result.points = pointsWrong
          } else if (answersRespondCount && answersCorrectCount === answersRespondCount) {
            result.points = pointsCorrect
          }
        }
      } else {
        result.pointsMax = pointsCorrect
        if (Object.keys(result.wrong).length) {
          result.points = pointsWrong
        } else if (Object.keys(result.correct).length) {
          result.points = pointsCorrect
        }
      }

      return result
    }
  }

  checkYesNo(slide: Slide, response: AttendeeResponse | undefined, correctResponse: SlideCorrectResponse | undefined) {

    const result: QuizSlideResult = {
      points: 0,
      pointsMax: 0,
      wrong: {},
      correct: {}
    }
    const pointsCorrect = slide.pointsCorrect === undefined ? 1 : slide.pointsCorrect
    const pointsWrong = slide.pointsWrong === undefined ? 0 : slide.pointsWrong
    response = (response || []) as { [key: string]: boolean }
    correctResponse = correctResponse || []

    for (const answer of  Object.values(slide.answers || {}).filter((answer: Answer) => !answer.deleted)) {
      const correct = correctResponse.indexOf(answer.id) !== -1
      const responded = response[ answer.id ] !== undefined

      result.correct[ answer.id ] = correct
      result.pointsMax += pointsCorrect

      if (correct !== response[ answer.id ]) {
        result.wrong[ answer.id ] = true
        result.points += pointsWrong
      } else if (responded) {
        result.points += pointsCorrect
      }
    }
    return result
  }

  checkOrder(slide: Slide, response: AttendeeResponse | undefined, correctResponse: SlideCorrectResponse | undefined) {

    const result: QuizSlideResult = {
      points: 0,
      pointsMax: 0,
      wrong: {},
      correct: {}
    }
    const pointsCorrect = slide.pointsCorrect === undefined ? 1 : slide.pointsCorrect
    const pointsWrong = slide.pointsWrong === undefined ? 0 : slide.pointsWrong
    response = (response || []) as string[]
    const answers =  Object.values(slide.answers || {}).filter((answer: Answer) => !answer.deleted)

    const correctOrder = {} as { [key: string]: number }
    const correctOrderList = [] as string[]
    const correctResponseId = correctResponse && correctResponse.length ? correctResponse[0] : null
    if (correctResponseId) {
      const length = answers.findIndex(x => x.id === correctResponseId)
      correctOrderList.push(...answers.slice(0, length === -1 ? answers.length : length).map(x => x.id))
    } else {
      correctOrderList.push(...answers.map(x => x.id))
    }

    let orderIndex = 1
    for (const correct of correctOrderList) {
      result.pointsMax += pointsCorrect
      result.correct[correct] = orderIndex
      correctOrder[ correct ] = orderIndex++
    }

    orderIndex = 1
    const responseOrder = {} as { [key: string]: number }
    const responseOrderList = [] as string[]
    for (const id of response) {
      if (answers.findIndex(x => x.id === id) !== -1) {
        responseOrder[ id ] = orderIndex++
        responseOrderList.push(id)
      }
    }

    for (const answer of answers) {
      const correct = correctOrder[answer.id] || 0
      const responded = responseOrder[answer.id] || 0

      if (correct !== responded) {
        result.wrong[ answer.id ] = true
        result.points += pointsWrong
      } else if (responded) {
        result.points += pointsCorrect
      }
    }

    if (!slide.pointsPerAnswer) {
      result.pointsMax = pointsCorrect
    }

    if (Object.keys(responseOrder).length) {
      if (!slide.pointsPerAnswer) {
        if (correctOrderList.length === responseOrderList.length && correctOrderList.every((value, index) => value === responseOrderList[index])) {
          result.points = pointsCorrect
        } else {
          result.points = pointsWrong
        }
      }
    } else {
      result.points = 0
    }

    return result
  }

  checkGaps(slide: Slide, response: AttendeeResponse | undefined, correctResponse: SlideCorrectResponse | undefined) {
    const result: QuizSlideResult = {
      points: 0,
      pointsMax: 0,
      wrong: {},
      correct: {}
    }
    const pointsCorrect = slide.pointsCorrect === undefined ? 1 : slide.pointsCorrect
    const pointsWrong = slide.pointsWrong === undefined ? 0 : slide.pointsWrong
    response = (response || []) as string[]
    correctResponse = correctResponse || []
    result.pointsMax = correctResponse.length * pointsCorrect

    for (const index in correctResponse) {
      result.correct[index] = correctResponse[index]
      let values = correctResponse[index].split('/').map(x => x.trim())
      let answer = response[index]?.trim() || ''

      if (slide.dropdown) {
        if (slide.selects) {
          result.correct[index] = values[0]
          values = [ values[0] ]
        }
      } else {
        if (slide.diacriticsOff) {
          values = values.map(x => latinize(x))
          answer = latinize(answer)
        }
        if (!slide.checkCase) {
          values = values.map(x => x.toLowerCase())
          answer = answer.toLowerCase()
        }
      }

      if (values.includes(answer)) {
        result.points += pointsCorrect
      } else {
        result.points += pointsWrong
        result.wrong[index] = true
      }
    }

    return result
  }

  checkNumber(slide: Slide, response: AttendeeResponse | undefined, correctResponse: SlideCorrectResponse | undefined, params: SlideParamsValuesSet | undefined) {
    const result: QuizSlideResult = {
      points: 0,
      pointsMax: 0,
      wrong: {},
      correct: {}
    }
    const pointsCorrect = slide.pointsCorrect === undefined ? 1 : slide.pointsCorrect
    const pointsWrong = slide.pointsWrong === undefined ? 0 : slide.pointsWrong
    result.pointsMax = pointsCorrect

    let correctResponseValue
    if (params) {
      correctResponseValue = params[ slide.paramsCorrect ] !== undefined ? +params[ slide.paramsCorrect ] : null
      result.correct.value = correctResponseValue || `[[${slide.paramsCorrect}]]`
    } else {
      correctResponseValue = correctResponse && correctResponse.length ? +correctResponse[0] : null
      result.correct.value = correctResponseValue || ''
    }

    if (correctResponseValue !== null && response !== undefined) {
      const responseValue = +response
      const correctTolarance = +slide.correctTolarance * 1.0001 // we need to extend tolarance by 0.01% due to float pointing computing issues
      const responseCorrectDiff = Math.abs(responseValue - correctResponseValue)
      // console.log(`${responseValue} - ${correctResponseValue} = |${Math.abs(responseValue - correctResponseValue)}| <= ${correctTolarance} [${Math.abs(responseValue - correctResponseValue) <= correctTolarance}]`)
      if (responseCorrectDiff <= correctTolarance) {
        result.points = pointsCorrect
      } else {
        result.points = pointsWrong
        result.wrong.response = true
      }
    } else if (correctResponseValue !== null) {
      result.wrong.response = true
    }

    return result
  }

  checkEssay(slide: Slide) {

    const result: QuizSlideResult = {
      points: 0,
      pointsMax: 0,
      wrong: {},
      correct: {}
    }
    const pointsCorrect = slide.pointsCorrect === undefined ? 1 : slide.pointsCorrect
    result.pointsMax = pointsCorrect
    return result
  }
}


/* ***************************************************************************
 * TopicStatistics
 * ***************************************************************************/

export class SlideStatistics {

  total = 0
  time = {
    data: new Array<number>(),
    min: 0, q1: 0, q2: 0, q3: 0, max: 0,
    avg: 0, std: 0
  }
  result: Json = {}
  stats: Json = {}

  constructor (slide: Slide, attendeeResponseResults: AttendeeResponseResult[]) {
    this.total = attendeeResponseResults.length
    this.setTime(slide, attendeeResponseResults)
    this.setStats(slide, attendeeResponseResults)
    if (['Choice', 'YesNo', 'Order', 'Number', 'Gaps', 'Essay'].includes(slide.type)) {
      this.setResult(slide, attendeeResponseResults)
    }
  }

  setTime (slide: Slide, attendeeResponseResults: AttendeeResponseResult[]) {

    for (const attendeeResponseResult of attendeeResponseResults) {
      const time = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.timems
      if (time) {
        this.time.data.push(time)
        if (!this.time.min || time < this.time.min) {
          this.time.min = time
        }
        if (!this.time.max || time > this.time.max) {
          this.time.max = time
        }
      }
    }

    if (this.time.data.length === 1) {
      this.time.avg = this.time.q1 = this.time.q2 = this.time.q3 = this.time.data[ 0 ]
    } else if (this.time.data.length > 1) {

      this.time.avg = this.time.data.reduce((sum, x) => sum + x, 0) / this.time.data.length
      this.time.std = Math.sqrt(this.time.data.reduce((sum, x) => sum + Math.pow(x - this.time.avg, 2), 0) / this.time.data.length)
      this.time.data.sort((a, b) => a - b)

      const q1position = Math.min(this.time.data.length, Math.max(1, (this.time.data.length + 1) / 4))
      this.time.q1 = this.time.data[ Math.floor(q1position) - 1 ]
      if (q1position < this.time.data.length) {
        this.time.q1 += (q1position % 1) * (this.time.data[ Math.floor(q1position) ] - this.time.data[ Math.floor(q1position) - 1 ])
      }

      const q2position = Math.min(this.time.data.length, Math.max(1, (this.time.data.length + 1) / 2))
      this.time.q2 = this.time.data[ Math.floor(q2position) - 1 ]
      if (q2position < this.time.data.length) {
        this.time.q2 += (q2position % 1) * (this.time.data[ Math.floor(q2position) ] - this.time.data[ Math.floor(q2position) - 1 ])
      }

      const q3position = Math.min(this.time.data.length, Math.max(1, (this.time.data.length + 1) * 3 / 4))
      this.time.q3 = this.time.data[ Math.floor(q3position) - 1 ]
      if (q3position < this.time.data.length) {
        this.time.q3 += (q3position % 1) * (this.time.data[ Math.floor(q3position) ] - this.time.data[ Math.floor(q3position) - 1 ])
      }
    }
  }

  setResult (slide: Slide, attendeeResponseResults: AttendeeResponseResult[]) {
    switch (slide.type) {
      case 'YesNo':
      case 'Gaps':
        const output1 = {} as Json
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response || {}
          const result = attendeeResponseResult.slides[ slide.id ]
          for (const id of Object.keys(result?.correct || {})) {
            output1[ id ] = output1[ id ] || {
              corrects: 0,
              wrongs: 0
            }
            if (response[ id ] !== undefined) {
              if (result?.wrong[ id ]) {
                output1[ id ][ 'wrongs' ] += 1
              } else {
                output1[ id ][ 'corrects' ] += 1
              }
            }
          }
        }
        this.result = output1
        break;
      default:
        const output2 = {
          corrects: 0,
          wrongs: 0
        }
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response !== undefined) {
            const result = attendeeResponseResult.slides[ slide.id ]
            if (result && result.points === result.pointsMax) {
              output2.corrects++
            } else {
              output2.wrongs++
            }
          }
        }
        this.result = output2
        break;
    }
  }

  setStats (slide: Slide, attendeeResponseResults: AttendeeResponseResult[]) {
    switch (slide.type) {
      case 'Choice':
      case 'Vote':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response) {
            for (const id of Object.keys(response)) {
              this.stats[ id ] = (this.stats[ id ] || 0) + 1
            }
          }
        }
        break
      case 'YesNo':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response) {
            for (const id of Object.keys(response)) {
              this.stats[ id ] = this.stats[ id ] || {}
              if (response[ id ]) {
                this.stats[ id ][ 'yes' ] = (this.stats[ id ][ 'yes' ] || 0) + 1
              } else {
                this.stats[ id ][ 'no' ] = (this.stats[ id ][ 'no' ] || 0) + 1
              }
            }
          }
        }
        break
      case 'Order':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response) {
            for (let index = 0; index < response.length; index++) {
              const id = response[ index ]
              this.stats[ id ] = this.stats[ id ] || {}
              this.stats[ id ][ index + 1 ] = (this.stats[ id ][ index + 1 ] || 0) + 1
            }
          }
        }
        break
      case 'Gaps':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response) {
            for (const key in response) {
              const value = response[ key ]
              this.stats[ key ] = this.stats[ key ] || {}
              this.stats[ key ][ value ] = (this.stats[ key ][ value ] || 0) + 1
            }
          }
        }
        break
      case 'Rate':
      case 'Number':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response !== undefined) {
            const value = `${response}`
            this.stats[ value ] = (this.stats[ value ] || 0) + 1
          }
        }
        break
      case 'Essay':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response && response.length) {
            const match = response[ 0 ].match(/[^\p{Z}]+/u)
            if (match) {
              const value = match[0].toLowerCase()
              this.stats[ value ] = (this.stats[ value ] || 0) + 1
            }
          }
        }
        break
      case 'Open':
        for (const attendeeResponseResult of attendeeResponseResults) {
          const response = attendeeResponseResult.attendeeItemData.attendee.data[ slide.id ]?.response
          if (response) {
            const match = response.match(/[^\p{Z}]+/u)
            if (match) {
              const value = match[0].toLowerCase()
              this.stats[ value ] = (this.stats[ value ] || 0) + 1
            }
          }
        }
        break
    }
  }
}

/* ***************************************************************************
 * StudentGradebook
 * ***************************************************************************/

export class StudentGradebook {

  results: Json = {
    total: 0
  }

  constructor (topics: Map<number, Item>, attendees: SessionAttendee[]) {
    const topicsWithPoints = new Map<number, Item>()
    for (const [id, topic] of topics) {
      const hasTopicPoints = (Object.values(topic.data?.slides || {}) as Slide[]).reduce((has: boolean, s: Slide) => has || ['Choice', 'YesNo', 'Order', 'Number', 'Gaps', 'Essay'].includes(s.type), false)
      if (hasTopicPoints) {
        topicsWithPoints.set(id, topic)
      }
    }
    for (const attendee of attendees) {
      const topicId = attendee.session?.topicId || 0
      const topic = topics.get(topicId)
      if (topic) {
        if (topicsWithPoints.has(topic.id)) {
          const topicResults = this.results[ topic.id ] || {
            present: true,
            results: new Map<number, AttendeeResponseResult>(),
            percent: 0
          }
          const attendeeItemData = new AttendeeItemData(topic.typeTopic as ItemTypeTopic, topic.data as ItemData, attendee)
          const attendeeResponseResult = new AttendeeResponseResult(attendeeItemData)
          topicResults.results.set(attendee.id, attendeeResponseResult)
          const topicGrades = [...topicResults.results.values()].map(r => r.percent)
          switch (topic.data?.info?.gradebook?.retakes) {
            case 'max':
              topicResults.percent = topicGrades.reduce((max: number, percent: number) => Math.max(max, percent), 0)
              break;
            case 'avg':
              topicResults.percent = topicGrades.reduce((sum: number, percent: number) => sum + percent, 0) / topicGrades.length
              break;
            case 'last':
              topicResults.percent = topicGrades[ topicGrades.length - 1 ]
              break;
            default:
              topicResults.percent = topicGrades[ 0 ]
              break;
          }
          this.results[ topic.id ] = topicResults
        } else {
          const topicResults = this.results[ topic.id ] || {
            present: true,
            results: new Map(),
            percent: null
          }
          this.results[ topic.id ] = topicResults
        }
      }
    }
    let totalPercentWeightedSum = 0
    let totalPercentSumOfWeights = 0
    for (const [id, topic] of topics) {
      this.results[ id ] = this.results[ id ] || {
        present: false,
        results: new Map(),
        percent: null
      }
      const weight = topic.data?.info?.gradebook?.weight
      if (weight) {
        totalPercentWeightedSum += weight * this.results[ id ].percent || 0
        totalPercentSumOfWeights += weight
      }
    }
    if (totalPercentSumOfWeights) {
      this.results.total = totalPercentWeightedSum / totalPercentSumOfWeights
    }
  }
}
